import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { useBudget } from '../../context/BudgetContext';
import { formatCAD } from '../../utils/formatCurrency';

const MONTH_KEYS = {
  es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  fr: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
};

export default function ProjectionChart() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const { derived } = useBudget();
  const [optimized, setOptimized] = useState(false);
  const chartRef = useRef(null);

  const months = MONTH_KEYS[lng] || MONTH_KEYS.es;
  const now = new Date();
  const startMonth = now.getMonth();

  const labels = Array.from({ length: 12 }, (_, i) => months[(startMonth + i) % 12]);

  // Base scenario
  const baseExpenses = derived.monthlyFixedExpenses + derived.monthlyVariableExpenses;
  const baseNet = derived.monthlyIncome - baseExpenses;

  // Optimized: -15% variable expenses + 2% cashback on all spending + HISA interest on savings
  const variableFactor = optimized ? 0.85 : 1;
  const cashbackRate = optimized ? 0.02 : 0;
  const hisaMonthlyRate = optimized ? 0.04 / 12 : 0; // 4% annual HISA
  const monthlyExpenses = derived.monthlyFixedExpenses + derived.monthlyVariableExpenses * variableFactor;
  const monthlyCashback = baseExpenses * cashbackRate;
  const monthlyNet = derived.monthlyIncome - monthlyExpenses + monthlyCashback;

  const incomeData = Array(12).fill(derived.monthlyIncome);
  const expenseData = Array(12).fill(monthlyExpenses);

  const cumulativeNet = [];
  let cumulative = 0;
  for (let i = 0; i < 12; i++) {
    const interest = cumulative > 0 ? cumulative * hisaMonthlyRate : 0;
    cumulative += monthlyNet + interest;
    cumulativeNet.push(cumulative);
  }

  // Also compute base cumulative for comparison when optimized
  const baseCumulativeNet = [];
  let baseCum = 0;
  for (let i = 0; i < 12; i++) {
    baseCum += baseNet;
    baseCumulativeNet.push(baseCum);
  }

  const annualSavingsDifference = optimized ? Math.round(cumulativeNet[11] - baseCumulativeNet[11]) : 0;

  const datasets = [
    {
      label: t('budgetPlanner.charts.income', 'Ingresos'),
      data: incomeData,
      borderColor: '#1E3A5F',
      backgroundColor: 'rgba(30,58,95,0.1)',
      tension: 0.3,
      fill: false,
    },
    {
      label: t('budgetPlanner.charts.expenses', 'Gastos'),
      data: expenseData,
      borderColor: '#7D5A00',
      backgroundColor: 'rgba(239,108,0,0.1)',
      tension: 0.3,
      fill: false,
    },
    {
      label: t('budgetPlanner.charts.cumulative', 'Ahorro acumulado'),
      data: cumulativeNet,
      borderColor: '#2E7D32',
      backgroundColor: 'rgba(46,125,50,0.1)',
      tension: 0.3,
      fill: true,
      borderDash: optimized ? [] : [5, 3],
    },
  ];

  // Show base line as ghost when optimized is on
  if (optimized) {
    datasets.push({
      label: t('budgetPlanner.charts.baseScenario', 'Sin optimizar'),
      data: baseCumulativeNet,
      borderColor: 'rgba(158,158,158,0.5)',
      backgroundColor: 'transparent',
      tension: 0.3,
      fill: false,
      borderDash: [5, 3],
      pointRadius: 0,
    });
  }

  const data = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${Math.round(ctx.raw).toLocaleString('en-CA')}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val) => `$${val.toLocaleString('en-CA')}`,
        },
      },
    },
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={optimized}
            onChange={(e) => setOptimized(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2E7D32' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2E7D32' },
            }}
          />
        }
        label={
          <Typography variant="body2" fontWeight={optimized ? 600 : 400}>
            {t('budgetPlanner.charts.optimizedToggle', 'Ver con optimización activada')}
          </Typography>
        }
        sx={{ mb: 1 }}
      />

      {optimized && annualSavingsDifference > 0 && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            '& .MuiAlert-icon': { color: '#2E7D32' },
            bgcolor: '#E8F5E9',
            border: '1px solid #A5D6A7',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {t('budgetPlanner.charts.optimizedSavings',
              'Con tarjeta 2% cashback + cuenta HISA, podrías ahorrar {{amount}} más este año.',
              { amount: formatCAD(annualSavingsDifference) }
            )}
          </Typography>
        </Alert>
      )}

      <Box sx={{ height: { xs: 240, md: 320 }, position: 'relative' }}>
        <Line ref={chartRef} data={data} options={options} />
      </Box>
    </Box>
  );
}
